import React from 'react';
import { css } from '@emotion/css';
import { Theme, useTheme, Typography, Chip, Box } from '@mui/material';
import AppDownloadBadges from '@components/AppDownloadBadges';
import { Features } from '@gql/base-types';
import { useGetPortalFeaturesQuery } from '../../queries/getPortalFeatures.query.generated';
import { PersonData } from './PersonStep';
import { AccessData } from './AccessTypeStep';
import { RooftopData, deriveRole } from './RooftopStep';
import { type DriverAttributeState } from './DriverAttributesStep';
import { formatPhone } from '@services/formatService';
import RoleChip from '../../components/RoleChip';
import { ROLE_DRIVER } from '../../utils/roleUtils';
import { ATTRIBUTE_META, type MobilityAttribute } from '@utils/productMapper';

interface ReviewStepProps {
  person: PersonData;
  access: AccessData;
  rooftop: RooftopData;
  featureIds: Set<number>;
  driverAttributes: DriverAttributeState;
  orgCustomerNames: Map<number, string>;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  person,
  access,
  rooftop,
  featureIds,
  driverAttributes,
  orgCustomerNames,
}) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { data } = useGetPortalFeaturesQuery();
  const allFeatures = (data?.features ?? []) as Features[];
  const selectedFeatures = allFeatures.filter(f => featureIds.has(f.id));

  const derivedRole = (!access.portalAccess && access.mobileAccess) ? ROLE_DRIVER : deriveRole(rooftop);

  const rooftopNames = rooftop.rooftopIds
    .map(id => orgCustomerNames.get(id))
    .filter(Boolean) as string[];

  // Get enabled driver attribute labels
  const enabledAttributeLabels = Object.entries(driverAttributes)
    .filter(([, val]) => val)
    .map(([key]) => ATTRIBUTE_META[key as MobilityAttribute]?.label)
    .filter(Boolean);

  return (
    <div>
      <Typography variant="overline" fontWeight={600} color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Review
      </Typography>

      {/* Person */}
      <div className={cls.section}>
        <Typography variant="body2" fontWeight={600} className={cls.sectionTitle}>
          Person
        </Typography>
        <Typography variant="body2">
          <strong>Name:</strong> {person.displayName}
        </Typography>
        <Typography variant="body2">
          <strong>Email:</strong> {person.email}
        </Typography>
        {person.phone && (
          <Typography variant="body2">
            <strong>Phone:</strong> {formatPhone(person.phone) ?? person.phone}
          </Typography>
        )}
      </div>

      <div className={cls.spacer} />

      {/* Access Type */}
      <div className={cls.section}>
        <Typography variant="body2" fontWeight={600} className={cls.sectionTitle}>
          Access
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {access.portalAccess && <Chip label="HopDrive Portal" size="small" color="primary" />}
          {access.mobileAccess && <Chip label="Mobile App" size="small" color="primary" />}
        </Box>
      </div>

      <div className={cls.spacer} />

      {/* Role & Rooftops */}
      <div className={cls.section}>
        <Typography variant="body2" fontWeight={600} className={cls.sectionTitle}>
          Role & Rooftops
        </Typography>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Role:</strong>
          </Typography>
          <RoleChip role={derivedRole} />
        </Box>
        {rooftop.isSuperAdmin && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Has access to all organization rooftops
          </Typography>
        )}
        {!rooftop.isSuperAdmin && rooftopNames.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {rooftopNames.map(name => (
              <Chip key={name} label={name} size="small" variant="outlined" />
            ))}
          </Box>
        )}
      </div>

      {/* Features */}
      {access.portalAccess && selectedFeatures.length > 0 && (
        <>
          <div className={cls.spacer} />
          <div className={cls.section}>
            <Typography variant="body2" fontWeight={600} className={cls.sectionTitle}>
              Features
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {selectedFeatures.map(f => (
                <Chip key={f.id} label={f.label} size="small" variant="outlined" />
              ))}
            </Box>
          </div>
        </>
      )}

      {/* Driver Attributes */}
      {access.mobileAccess && enabledAttributeLabels.length > 0 && (
        <>
          <div className={cls.spacer} />
          <div className={cls.section}>
            <Typography variant="body2" fontWeight={600} className={cls.sectionTitle}>
              Driver Attributes
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {enabledAttributeLabels.map(label => (
                <Chip key={label} label={label} size="small" variant="outlined" />
              ))}
            </Box>
          </div>
        </>
      )}

      {/* Mobile App Download */}
      {access.mobileAccess && (
        <>
          <div className={cls.spacer} />
          <div className={cls.section}>
            <AppDownloadBadges
              compact
              label="Have the new team member download the HopDrive Driver app to get started."
            />
          </div>
        </>
      )}
    </div>
  );
};

const useStyles = (theme?: Theme) => ({
  section: css`
    padding: 12px;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    background-color: ${theme?.palette?.action?.hover};
  `,
  sectionTitle: css`
    margin-bottom: 8px;
  `,
  spacer: css`
    width: 100%;
    height: 12px;
  `,
});

export default ReviewStep;
